/**
 * Accreditation Monitoring & Renewal Dashboard
 * Covers: course renewal lifecycle, annual monitoring visits,
 * spot checks, evidence refresh, and renewal scoring.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  FilterPills, Tabs, Alert, Btn, FormField, Card,
  inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const MON  = `${BASE}/accreditation-monitoring`;
const ACC  = `${BASE}/course-accreditations`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function ScorePill({ score, band }) {
  if (score == null) return <span style={{ color: colors.textMuted }}>—</span>;
  const colors_ = {
    excellent:           { bg: "#dcfce7", text: "#15803d" },
    satisfactory:        { bg: "#dbeafe", text: "#1d4ed8" },
    requires_improvement:{ bg: "#fef9c3", text: "#b45309" },
    unsatisfactory:      { bg: "#fee2e2", text: "#b91c1c" },
  };
  const s = colors_[band] || { bg: colors.backgroundAlt, text: colors.textMuted };
  return (
    <span style={{
      background: s.bg, color: s.text,
      border: `1px solid ${s.text}44`,
      borderRadius: radius.pill,
      padding: "2px 10px",
      fontSize: typography.fontSizes.sm,
      fontWeight: typography.fontWeights.semibold,
    }}>
      {score}% · {band ? band.replace(/_/g, " ") : ""}
    </span>
  );
}

function FindingBadge({ finding }) {
  if (!finding) return <span style={{ color: colors.textMuted }}>—</span>;
  const map = {
    satisfactory:    { bg: "#dcfce7", text: "#15803d" },
    minor_concerns:  { bg: "#fef9c3", text: "#b45309" },
    major_concerns:  { bg: "#fed7aa", text: "#c2410c" },
    critical_failure:{ bg: "#fee2e2", text: "#b91c1c" },
  };
  const s = map[finding] || { bg: colors.backgroundAlt, text: colors.textMuted };
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.text}44`,
      borderRadius: radius.pill, padding: "2px 10px",
      fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
    }}>
      {finding.replace(/_/g, " ")}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AccreditationMonitoring() {
  const navigate   = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");

  const [dashboard,  setDashboard]  = useState(null);
  const [renewals,   setRenewals]   = useState([]);
  const [visits,     setVisits]     = useState([]);
  const [accreds,    setAccreds]    = useState([]);
  const [loading,    setLoading]    = useState(true);

  const [filterRenewalStatus, setFilterRenewalStatus] = useState("all");
  const [filterVisitStatus,   setFilterVisitStatus]   = useState("all");
  const [filterVisitType,     setFilterVisitType]     = useState("all");

  // Modals
  const [showRenewalModal,  setShowRenewalModal]  = useState(false);
  const [showVisitModal,    setShowVisitModal]    = useState(false);
  const [showScoreModal,    setShowScoreModal]    = useState(null);   // renewal record
  const [showDecideModal,   setShowDecideModal]   = useState(null);   // renewal record
  const [showCompleteModal, setShowCompleteModal] = useState(null);   // visit record
  const [showRefreshModal,  setShowRefreshModal]  = useState(null);   // {renewalId, items[]}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState("");
  const [msgType, setMsgType] = useState("success");

  // Form state
  const [renewalForm, setRenewalForm] = useState({ accreditation_id: "", notes: "" });
  const [visitForm,   setVisitForm]   = useState({ accreditation_id: "", visit_type: "", scheduled_date: "", notes: "" });
  const [scoreForm,   setScoreForm]   = useState({ evidence_score: "", compliance_score: "" });
  const [decideForm,  setDecideForm]  = useState({ decision: "", rationale: "", renewed_until: "" });
  const [completeForm, setCompleteForm] = useState({
    overall_finding: "", findings_summary: "", visit_score: "", recommendations: "",
    requires_follow_up: false, follow_up_due_date: "",
  });
  const [ncInput, setNcInput]     = useState("");
  const [ncList,  setNcList]      = useState([]);

  const token   = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const flash   = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 4000); };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, rRes, vRes, aRes] = await Promise.all([
        fetch(`${MON}/dashboard`, { headers }),
        fetch(`${MON}/renewals`, { headers }),
        fetch(`${MON}/visits`, { headers }),
        fetch(`${ACC}/`, { headers }),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (rRes.ok) setRenewals(await rRes.json());
      if (vRes.ok) setVisits(await vRes.json());
      if (aRes.ok) setAccreds(await aRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Submit handlers ──────────────────────────────────────────────────────

  async function submitRenewal(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${MON}/renewals`, {
        method: "POST", headers,
        body: JSON.stringify({ accreditation_id: parseInt(renewalForm.accreditation_id), notes: renewalForm.notes || null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Renewal initiated. Evidence refresh items created for all EV categories.");
      setShowRenewalModal(false);
      setRenewalForm({ accreditation_id: "", notes: "" });
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function submitVisit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${MON}/visits`, {
        method: "POST", headers,
        body: JSON.stringify({
          accreditation_id: parseInt(visitForm.accreditation_id),
          visit_type: visitForm.visit_type,
          scheduled_date: visitForm.scheduled_date || null,
          notes: visitForm.notes || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Visit scheduled successfully.");
      setShowVisitModal(false);
      setVisitForm({ accreditation_id: "", visit_type: "", scheduled_date: "", notes: "" });
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function submitScore(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${MON}/renewals/${showScoreModal.id}/score`, {
        method: "POST", headers,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Score computed from submitted evidence and visit outcomes.");
      setShowScoreModal(null);
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function submitManualScore(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {};
      if (scoreForm.evidence_score !== "") body.evidence_score = parseFloat(scoreForm.evidence_score);
      if (scoreForm.compliance_score !== "") body.compliance_score = parseFloat(scoreForm.compliance_score);
      const res = await fetch(`${MON}/renewals/${showScoreModal.id}`, {
        method: "PATCH", headers, body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Scores updated.");
      setShowScoreModal(null);
      setScoreForm({ evidence_score: "", compliance_score: "" });
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function submitDecide(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${MON}/renewals/${showDecideModal.id}/decision`, {
        method: "POST", headers,
        body: JSON.stringify({
          decision: decideForm.decision,
          rationale: decideForm.rationale,
          renewed_until: decideForm.renewed_until || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Renewal decision recorded.");
      setShowDecideModal(null);
      setDecideForm({ decision: "", rationale: "", renewed_until: "" });
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function submitComplete(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${MON}/visits/${showCompleteModal.id}/complete`, {
        method: "POST", headers,
        body: JSON.stringify({
          overall_finding:   completeForm.overall_finding,
          findings_summary:  completeForm.findings_summary,
          nonconformities:   ncList.length > 0 ? ncList : null,
          recommendations:   completeForm.recommendations || null,
          visit_score:       completeForm.visit_score ? parseFloat(completeForm.visit_score) : null,
          requires_follow_up: completeForm.requires_follow_up,
          follow_up_due_date: completeForm.follow_up_due_date || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Visit outcome recorded.");
      setShowCompleteModal(null);
      setCompleteForm({ overall_finding: "", findings_summary: "", visit_score: "", recommendations: "", requires_follow_up: false, follow_up_due_date: "" });
      setNcList([]);
      fetchAll();
    } catch (err) { flash(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function loadRefreshItems(renewalId) {
    const res = await fetch(`${MON}/renewals/${renewalId}/evidence-refresh`, { headers });
    if (res.ok) {
      const items = await res.json();
      setShowRefreshModal({ renewalId, items });
    }
  }

  async function updateRefreshItem(itemId, status) {
    await fetch(`${MON}/evidence-refresh/${itemId}`, {
      method: "PATCH", headers,
      body: JSON.stringify({ status }),
    });
    loadRefreshItems(showRefreshModal.renewalId);
  }

  // ── Filtered data ────────────────────────────────────────────────────────

  const filteredRenewals = renewals.filter(r =>
    filterRenewalStatus === "all" || r.status === filterRenewalStatus
  );
  const filteredVisits = visits.filter(v =>
    (filterVisitStatus === "all" || v.status === filterVisitStatus) &&
    (filterVisitType   === "all" || v.visit_type === filterVisitType)
  );

  // ── KPIs ──────────────────────────────────────────────────────────────────

  const kpis = [
    { label: "Active Renewals",      value: dashboard?.active_renewals ?? "—",             icon: "♻️", color: colors.primary },
    { label: "Upcoming Visits (30d)", value: dashboard?.upcoming_visits?.length ?? "—",    icon: "📅", color: colors.info },
    { label: "Overdue Follow-ups",    value: dashboard?.overdue_followup_count ?? "—",     icon: "⚠️", color: colors.danger },
    { label: "Expiring (90d)",        value: dashboard?.expiring_accreditations_count ?? "—", icon: "⏱", color: colors.warning },
  ];

  // ── Columns ───────────────────────────────────────────────────────────────

  const renewalCols = [
    { key: "id", label: "#", render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span> },
    { key: "accreditation_id", label: "Accreditation", render: (v, r) => {
      const a = accreds.find(x => x.id === v);
      return <span style={{ fontWeight: typography.fontWeights.semibold }}>{a?.course_title || `#${v}`}</span>;
    }},
    { key: "status", label: "Status", render: v => <StatusBadge status={v} customLabel={v?.replace(/_/g," ")} /> },
    { key: "overall_score", label: "Score", render: (v, r) => <ScorePill score={v} band={r.score_band} /> },
    { key: "renewal_due_date", label: "Due", render: (v, r) => {
      if (!v) return "—";
      const color = r.days_to_due < 0 ? colors.danger : r.days_to_due <= 30 ? colors.warning : colors.textPrimary;
      return <span style={{ color }}>{fmtDate(v)}{r.days_to_due != null && ` (${r.days_to_due}d)`}</span>;
    }},
    { key: "renewed_until", label: "Renewed Until", render: v => fmtDate(v) },
    { key: "_act", label: "", align: "right", render: (_, r) => (
      <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Btn size="sm" variant="ghost" onClick={() => loadRefreshItems(r.id)}>Evidence</Btn>
        <Btn size="sm" variant="secondary" onClick={() => { setShowScoreModal(r); setScoreForm({ evidence_score: r.evidence_score ?? "", compliance_score: r.compliance_score ?? "" }); }}>Score</Btn>
        {!["renewed","rejected"].includes(r.status) && (
          <Btn size="sm" variant="primary" onClick={() => { setShowDecideModal(r); setDecideForm({ decision: "", rationale: "", renewed_until: "" }); }}>Decide</Btn>
        )}
      </div>
    )},
  ];

  const visitCols = [
    { key: "id", label: "#", render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span> },
    { key: "accreditation_id", label: "Accreditation", render: v => {
      const a = accreds.find(x => x.id === v);
      return <span style={{ fontWeight: typography.fontWeights.semibold }}>{a?.course_title || `#${v}`}</span>;
    }},
    { key: "visit_type", label: "Type", render: v => <span style={{ fontSize: typography.fontSizes.sm }}>{v?.replace(/_/g, " ")}</span> },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} /> },
    { key: "scheduled_date", label: "Scheduled", render: v => fmtDate(v) },
    { key: "overall_finding", label: "Finding", render: v => <FindingBadge finding={v} /> },
    { key: "visit_score", label: "Score", render: v => v != null ? `${v}%` : "—" },
    { key: "_act", label: "", align: "right", render: (_, v) => v.status !== "completed" && (
      <Btn size="sm" variant="primary"
        onClick={() => { setShowCompleteModal(v); setNcList([]); setCompleteForm({ overall_finding: "", findings_summary: "", visit_score: "", recommendations: "", requires_follow_up: false, follow_up_due_date: "" }); }}>
        Complete
      </Btn>
    )},
  ];

  const tabs = [
    { key: "dashboard", label: "Dashboard",  icon: "📊" },
    { key: "renewals",  label: "Renewals",   icon: "♻️", count: renewals.filter(r => !["renewed","rejected"].includes(r.status)).length },
    { key: "visits",    label: "Visits",     icon: "📅", count: visits.filter(v => v.status !== "completed").length },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: `${spacing.lg} ${spacing.xl}` }}>
      <PageHeader
        title="Monitoring & Renewal"
        subtitle="Course accreditation renewal cycles, annual monitoring visits, and spot checks."
        actions={
          <div style={{ display: "flex", gap: spacing.sm }}>
            <Btn variant="secondary" onClick={() => setShowVisitModal(true)}>+ Schedule Visit</Btn>
            <Btn variant="primary"   onClick={() => setShowRenewalModal(true)}>+ Initiate Renewal</Btn>
          </div>
        }
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* ── DASHBOARD TAB ── */}
      {activeTab === "dashboard" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.xl }}>

          {/* Expiring accreditations */}
          <Card>
            <div style={{ fontWeight: typography.fontWeights.semibold, marginBottom: spacing.md, color: colors.warning }}>
              Accreditations Expiring (90 days) — No Active Renewal
            </div>
            {!dashboard?.expiring_accreditations?.length ? (
              <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>None — all on track.</div>
            ) : dashboard.expiring_accreditations.map(a => (
              <div key={a.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: `${spacing.xs} 0`,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <span style={{ fontSize: typography.fontSizes.sm }}>{a.course_title}</span>
                <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                  <span style={{ fontSize: typography.fontSizes.xs, color: a.days_to_expiry <= 30 ? colors.danger : colors.warning }}>
                    {a.days_to_expiry}d left
                  </span>
                  <Btn size="sm" variant="warning" onClick={() => { setRenewalForm({ accreditation_id: String(a.id), notes: "" }); setShowRenewalModal(true); }}>
                    Initiate
                  </Btn>
                </div>
              </div>
            ))}
          </Card>

          {/* Upcoming visits */}
          <Card>
            <div style={{ fontWeight: typography.fontWeights.semibold, marginBottom: spacing.md }}>
              Upcoming Visits (next 30 days)
            </div>
            {!dashboard?.upcoming_visits?.length ? (
              <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>No visits scheduled in the next 30 days.</div>
            ) : dashboard.upcoming_visits.map(v => (
              <div key={v.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: `${spacing.xs} 0`,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <div>
                  <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium }}>
                    {v.visit_type?.replace(/_/g, " ")}
                  </div>
                  <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>{fmtDate(v.scheduled_date)}</div>
                </div>
                <Btn size="sm" variant="primary"
                  onClick={() => { setShowCompleteModal(v); setNcList([]); setCompleteForm({ overall_finding: "", findings_summary: "", visit_score: "", recommendations: "", requires_follow_up: false, follow_up_due_date: "" }); }}>
                  Record
                </Btn>
              </div>
            ))}
          </Card>

          {/* Overdue follow-ups */}
          {dashboard?.overdue_followup_count > 0 && (
            <Card accent={colors.danger} style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontWeight: typography.fontWeights.semibold, color: colors.danger, marginBottom: spacing.md }}>
                Overdue Follow-ups ({dashboard.overdue_followup_count})
              </div>
              {dashboard.overdue_followups.map(v => (
                <div key={v.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: `${spacing.xs} 0`, borderBottom: `1px solid ${colors.border}`,
                }}>
                  <div>
                    <span style={{ fontSize: typography.fontSizes.sm }}>{v.visit_type?.replace(/_/g," ")}</span>
                    <span style={{ fontSize: typography.fontSizes.xs, color: colors.danger, marginLeft: spacing.sm }}>
                      Due {fmtDate(v.follow_up_due_date)}
                    </span>
                  </div>
                  <FindingBadge finding={v.overall_finding} />
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* ── RENEWALS TAB ── */}
      {activeTab === "renewals" && (
        <>
          <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
            <FilterPills
              options={[
                { value: "all",              label: "All" },
                { value: "initiated",        label: "Initiated" },
                { value: "evidence_refresh", label: "Evidence Refresh" },
                { value: "scoring",          label: "Scoring" },
                { value: "decision_pending", label: "Decision Pending" },
                { value: "renewed",          label: "Renewed" },
                { value: "rejected",         label: "Rejected" },
              ]}
              value={filterRenewalStatus}
              onChange={setFilterRenewalStatus}
            />
          </div>
          <DataTable
            columns={renewalCols}
            rows={filteredRenewals}
            loading={loading}
            emptyMessage="No renewal records found."
          />
        </>
      )}

      {/* ── VISITS TAB ── */}
      {activeTab === "visits" && (
        <>
          <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
            <FilterPills
              options={[
                { value: "all",         label: "All" },
                { value: "scheduled",   label: "Scheduled" },
                { value: "in_progress", label: "In Progress" },
                { value: "completed",   label: "Completed" },
                { value: "overdue",     label: "Overdue" },
              ]}
              value={filterVisitStatus}
              onChange={setFilterVisitStatus}
            />
            <select
              className="ds-input"
              value={filterVisitType}
              onChange={e => setFilterVisitType(e.target.value)}
              style={{ ...selectStyle, width: "auto" }}
            >
              <option value="all">All Types</option>
              {["annual_monitoring","spot_check","unannounced","follow_up","pre_renewal","complaint_triggered"].map(t => (
                <option key={t} value={t}>{t.replace(/_/g," ")}</option>
              ))}
            </select>
          </div>
          <DataTable
            columns={visitCols}
            rows={filteredVisits}
            loading={loading}
            emptyMessage="No monitoring visits found."
          />
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          INITIATE RENEWAL MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showRenewalModal && (
        <StandardModal
          title="Initiate Renewal Cycle"
          onClose={() => setShowRenewalModal(false)}
          width={500}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowRenewalModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="renewal-form" loading={saving}>Initiate Renewal</Btn>
            </>
          }
        >
          <form id="renewal-form" onSubmit={submitRenewal}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Course Accreditation" htmlFor="ren-acc" required>
                <select id="ren-acc" className="ds-input" value={renewalForm.accreditation_id}
                  onChange={e => setRenewalForm(f => ({ ...f, accreditation_id: e.target.value }))}
                  required style={selectStyle}>
                  <option value="">— Select accreditation —</option>
                  {accreds.map(a => (
                    <option key={a.id} value={a.id}>{a.course_title || `Accreditation #${a.id}`} ({a.status})</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Notes" htmlFor="ren-notes">
                <textarea id="ren-notes" className="ds-input" value={renewalForm.notes}
                  onChange={e => setRenewalForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3} placeholder="Optional initiation notes…" style={textareaStyle} />
              </FormField>
              <div style={{ background: colors.infoLight, border: `1px solid ${colors.info}44`, borderRadius: radius.md, padding: spacing.sm, fontSize: typography.fontSizes.sm, color: colors.info }}>
                Initiating a renewal will auto-create evidence refresh items for all 15 EV categories.
                The renewal window is set to 90 days before the current expiry date.
              </div>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SCHEDULE VISIT MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showVisitModal && (
        <StandardModal
          title="Schedule Monitoring Visit"
          onClose={() => setShowVisitModal(false)}
          width={500}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowVisitModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="visit-form" loading={saving}>Schedule</Btn>
            </>
          }
        >
          <form id="visit-form" onSubmit={submitVisit}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Course Accreditation" htmlFor="vis-acc" required>
                <select id="vis-acc" className="ds-input" value={visitForm.accreditation_id}
                  onChange={e => setVisitForm(f => ({ ...f, accreditation_id: e.target.value }))}
                  required style={selectStyle}>
                  <option value="">— Select accreditation —</option>
                  {accreds.map(a => (
                    <option key={a.id} value={a.id}>{a.course_title || `Accreditation #${a.id}`}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Visit Type" htmlFor="vis-type" required>
                <select id="vis-type" className="ds-input" value={visitForm.visit_type}
                  onChange={e => setVisitForm(f => ({ ...f, visit_type: e.target.value }))}
                  required style={selectStyle}>
                  <option value="">— Select type —</option>
                  {["annual_monitoring","spot_check","unannounced","follow_up","pre_renewal","complaint_triggered"].map(t => (
                    <option key={t} value={t}>{t.replace(/_/g," ")}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Scheduled Date" htmlFor="vis-date">
                <input id="vis-date" className="ds-input" type="date" value={visitForm.scheduled_date}
                  onChange={e => setVisitForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  style={inputStyle} />
              </FormField>
              <FormField label="Notes" htmlFor="vis-notes">
                <textarea id="vis-notes" className="ds-input" value={visitForm.notes}
                  onChange={e => setVisitForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} style={textareaStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SCORING MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showScoreModal && (
        <StandardModal
          title={`Renewal Scoring — #${showScoreModal.id}`}
          onClose={() => setShowScoreModal(null)}
          width={540}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowScoreModal(null)}>Close</Btn>
              <Btn variant="secondary" onClick={submitScore} loading={saving}>Auto-compute from Evidence</Btn>
              <Btn variant="primary" type="submit" form="score-form" loading={saving}>Set Manual Scores</Btn>
            </>
          }
        >
          <div style={{ marginBottom: spacing.md }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
              {[
                ["Evidence Score", showScoreModal.evidence_score],
                ["Compliance Score", showScoreModal.compliance_score],
                ["Overall Score", showScoreModal.overall_score],
              ].map(([label, val]) => (
                <div key={label} style={{ textAlign: "center", background: colors.backgroundAlt, borderRadius: radius.md, padding: spacing.md }}>
                  <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.primary }}>
                    {val != null ? `${val}%` : "—"}
                  </div>
                </div>
              ))}
            </div>
            {showScoreModal.score_band && (
              <div style={{ textAlign: "center", marginBottom: spacing.md }}>
                <ScorePill score={showScoreModal.overall_score} band={showScoreModal.score_band} />
              </div>
            )}
            <div style={{ background: colors.infoLight, border: `1px solid ${colors.info}44`, borderRadius: radius.md, padding: spacing.sm, fontSize: typography.fontSizes.sm, color: colors.info, marginBottom: spacing.md }}>
              <strong>Auto-compute</strong> calculates evidence score from accepted EV items (60% weight)
              and compliance score from monitoring visit outcomes (40% weight).
            </div>
          </div>
          <form id="score-form" onSubmit={submitManualScore}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md }}>
              <FormField label="Evidence Score (0-100)" htmlFor="sc-ev">
                <input id="sc-ev" className="ds-input" type="number" min="0" max="100"
                  value={scoreForm.evidence_score}
                  onChange={e => setScoreForm(f => ({ ...f, evidence_score: e.target.value }))}
                  style={inputStyle} />
              </FormField>
              <FormField label="Compliance Score (0-100)" htmlFor="sc-comp">
                <input id="sc-comp" className="ds-input" type="number" min="0" max="100"
                  value={scoreForm.compliance_score}
                  onChange={e => setScoreForm(f => ({ ...f, compliance_score: e.target.value }))}
                  style={inputStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          RENEWAL DECISION MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showDecideModal && (
        <StandardModal
          title={`Renewal Decision — #${showDecideModal.id}`}
          onClose={() => setShowDecideModal(null)}
          width={560}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowDecideModal(null)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="decide-form" loading={saving}>Record Decision</Btn>
            </>
          }
        >
          <form id="decide-form" onSubmit={submitDecide}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              {showDecideModal.overall_score != null && (
                <div style={{ textAlign: "center", marginBottom: spacing.xs }}>
                  <ScorePill score={showDecideModal.overall_score} band={showDecideModal.score_band} />
                  <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 4 }}>Current renewal score</div>
                </div>
              )}
              <FormField label="Decision" htmlFor="dec-type" required>
                <select id="dec-type" className="ds-input" value={decideForm.decision}
                  onChange={e => setDecideForm(f => ({ ...f, decision: e.target.value }))}
                  required style={selectStyle}>
                  <option value="">— Select outcome —</option>
                  <option value="renewed">Renew — full accreditation renewed</option>
                  <option value="conditional_renewal">Conditional Renewal — conditions apply</option>
                  <option value="rejected">Reject Renewal — accreditation lapses</option>
                </select>
              </FormField>
              {decideForm.decision === "renewed" && (
                <FormField label="Renewed Until" htmlFor="dec-expiry"
                  helper="Leave blank to default to 36 months from today.">
                  <input id="dec-expiry" className="ds-input" type="date" value={decideForm.renewed_until}
                    onChange={e => setDecideForm(f => ({ ...f, renewed_until: e.target.value }))}
                    style={inputStyle} />
                </FormField>
              )}
              <FormField label="Rationale" htmlFor="dec-rat" required>
                <textarea id="dec-rat" className="ds-input" value={decideForm.rationale}
                  onChange={e => setDecideForm(f => ({ ...f, rationale: e.target.value }))}
                  required rows={4}
                  placeholder="Document the basis for this renewal decision, evidence reviewed, and committee agreement…"
                  style={textareaStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          COMPLETE VISIT MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showCompleteModal && (
        <StandardModal
          title="Record Visit Outcome"
          onClose={() => setShowCompleteModal(null)}
          width={600}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCompleteModal(null)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="complete-form" loading={saving}>Save Outcome</Btn>
            </>
          }
        >
          <form id="complete-form" onSubmit={submitComplete}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Overall Finding" htmlFor="comp-find" required>
                <select id="comp-find" className="ds-input" value={completeForm.overall_finding}
                  onChange={e => setCompleteForm(f => ({ ...f, overall_finding: e.target.value }))}
                  required style={selectStyle}>
                  <option value="">— Select finding —</option>
                  <option value="satisfactory">Satisfactory</option>
                  <option value="minor_concerns">Minor Concerns</option>
                  <option value="major_concerns">Major Concerns</option>
                  <option value="critical_failure">Critical Failure</option>
                </select>
              </FormField>
              <FormField label="Findings Summary" htmlFor="comp-summary" required>
                <textarea id="comp-summary" className="ds-input" value={completeForm.findings_summary}
                  onChange={e => setCompleteForm(f => ({ ...f, findings_summary: e.target.value }))}
                  required rows={4}
                  placeholder="Describe the key findings from the visit, areas of compliance, and any concerns identified…"
                  style={textareaStyle} />
              </FormField>
              <FormField label="Non-conformities" htmlFor="comp-nc"
                helper="Enter each NC and press Enter or Add. Leave blank if none.">
                <div style={{ display: "flex", gap: spacing.xs, marginBottom: spacing.xs }}>
                  <input id="comp-nc" className="ds-input" type="text" value={ncInput}
                    onChange={e => setNcInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (ncInput.trim()) { setNcList(l => [...l, ncInput.trim()]); setNcInput(""); }}}}
                    placeholder="Describe non-conformity…" style={{ ...inputStyle, flex: 1 }} />
                  <Btn variant="secondary" size="sm" type="button"
                    onClick={() => { if (ncInput.trim()) { setNcList(l => [...l, ncInput.trim()]); setNcInput(""); }}}>
                    Add
                  </Btn>
                </div>
                {ncList.map((nc, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: colors.dangerLight, border: `1px solid ${colors.danger}44`,
                    borderRadius: radius.sm, padding: "4px 10px", marginBottom: 4,
                  }}>
                    <span style={{ fontSize: typography.fontSizes.sm }}>{nc}</span>
                    <button type="button" onClick={() => setNcList(l => l.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: colors.textMuted }}>✕</button>
                  </div>
                ))}
              </FormField>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md }}>
                <FormField label="Visit Score (0-100)" htmlFor="comp-score">
                  <input id="comp-score" className="ds-input" type="number" min="0" max="100"
                    value={completeForm.visit_score}
                    onChange={e => setCompleteForm(f => ({ ...f, visit_score: e.target.value }))}
                    style={inputStyle} />
                </FormField>
                <FormField label="Recommendations" htmlFor="comp-rec">
                  <textarea id="comp-rec" className="ds-input" value={completeForm.recommendations}
                    onChange={e => setCompleteForm(f => ({ ...f, recommendations: e.target.value }))}
                    rows={2} style={textareaStyle} />
                </FormField>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
                <input type="checkbox" checked={completeForm.requires_follow_up}
                  onChange={e => setCompleteForm(f => ({ ...f, requires_follow_up: e.target.checked }))}
                  style={{ accentColor: colors.warning }} />
                <span style={{ fontSize: typography.fontSizes.sm }}>Requires follow-up visit</span>
              </label>
              {completeForm.requires_follow_up && (
                <FormField label="Follow-up Due Date" htmlFor="comp-fu">
                  <input id="comp-fu" className="ds-input" type="date" value={completeForm.follow_up_due_date}
                    onChange={e => setCompleteForm(f => ({ ...f, follow_up_due_date: e.target.value }))}
                    style={inputStyle} />
                </FormField>
              )}
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          EVIDENCE REFRESH MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showRefreshModal && (
        <StandardModal
          title={`Evidence Refresh — Renewal #${showRefreshModal.renewalId}`}
          onClose={() => setShowRefreshModal(null)}
          width={640}
          footer={<Btn variant="secondary" onClick={() => setShowRefreshModal(null)}>Close</Btn>}
        >
          <div style={{ marginBottom: spacing.sm, fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
            Review and accept or reject each evidence refresh item. The renewal score is calculated from accepted items.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            {showRefreshModal.items.map(item => {
              const statusColors = {
                outstanding: { bg: colors.backgroundAlt,  text: colors.textMuted },
                submitted:   { bg: colors.infoLight,      text: colors.info },
                accepted:    { bg: colors.successLight,   text: colors.success },
                rejected:    { bg: colors.dangerLight,    text: colors.danger },
              };
              const sc = statusColors[item.status] || statusColors.outstanding;
              return (
                <div key={item.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: sc.bg, border: `1px solid ${sc.text}33`,
                  borderRadius: radius.md, padding: `${spacing.xs} ${spacing.md}`,
                }}>
                  <div>
                    <span style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>
                      {item.requirement_category}
                    </span>
                    <span style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginLeft: spacing.xs }}>
                      {item.requirement_name}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
                    <span style={{
                      fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
                      color: sc.text, background: "white", borderRadius: radius.pill,
                      padding: "1px 8px", border: `1px solid ${sc.text}44`,
                    }}>
                      {item.status}
                    </span>
                    {item.status === "submitted" && (
                      <>
                        <Btn size="sm" variant="success" onClick={() => updateRefreshItem(item.id, "accepted")}>Accept</Btn>
                        <Btn size="sm" variant="danger"  onClick={() => updateRefreshItem(item.id, "rejected")}>Reject</Btn>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: spacing.md, fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            Accepted: {showRefreshModal.items.filter(i => i.status === "accepted").length} /
            {showRefreshModal.items.length} items —
            Evidence score: {showRefreshModal.items.length > 0 ? Math.round(showRefreshModal.items.filter(i => i.status === "accepted").length / showRefreshModal.items.length * 100) : 0}%
          </div>
        </StandardModal>
      )}
    </div>
  );
}
